import React, { useState, useEffect, useRef } from "react";

const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;
const CHANNEL_ID = process.env.REACT_APP_YOUTUBE_CHANNEL_ID;

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [tokensHistory, setTokensHistory] = useState({ 1: "" });
  const [nextPageToken, setNextPageToken] = useState(null);

  const isFetchingRef = useRef(false);

  useEffect(() => {
    const fetchChannelStats = async () => {
      try {
        const response = await fetch(
          `https://www.googleapis.com/youtube/v3/channels?key=${API_KEY}&id=${CHANNEL_ID}&part=statistics`
        );
        if (!response.ok) throw new Error();
        const data = await response.json();
        
        if (data.items && data.items[0]) {
          const totalVideos = parseInt(data.items[0].statistics.videoCount, 10);
          const pages = Math.ceil(totalVideos / 12);
          setTotalPages(pages || 1);
        }
      } catch (err) {
        console.error("Impossible de récupérer les statistiques de la chaîne", err);
      }
    };
    fetchChannelStats();
  }, []);

  useEffect(() => {
    const fetchYouTubeVideos = async () => {
      if (tokensHistory[currentPage] === undefined || isFetchingRef.current) return;

      setLoading(true);
      isFetchingRef.current = true;

      try {
        const currentToken = tokensHistory[currentPage];
        const url = `https://www.googleapis.com/youtube/v3/search?key=${API_KEY}&channelId=${CHANNEL_ID}&part=snippet,id&order=date&maxResults=12&type=video&pageToken=${currentToken}`;

        const response = await fetch(url);
        if (!response.ok) throw new Error("Impossible de récupérer les vidéos de YouTube.");

        const data = await response.json();
        setNextPageToken(data.nextPageToken || null);

        if (data.nextPageToken && tokensHistory[currentPage + 1] === undefined) {
          setTokensHistory((prev) => ({ ...prev, [currentPage + 1]: data.nextPageToken }));
        }

        const formattedVideos = data.items.map((item) => ({
          id: item.id.videoId,
          title: item.snippet.title,
          description: item.snippet.description,
          url: `https://www.youtube.com/embed/${item.id.videoId}`,
          watchUrl: `https://www.youtube.com/watch?v=${item.id.videoId}`,
        }));

        setVideos(formattedVideos);
      } catch (err) {
        setError(err.message);
      } finally {
        isFetchingRef.current = false;
        setTimeout(() => setLoading(false), 300);
      }
    };

    fetchYouTubeVideos();
  }, [currentPage, tokensHistory]);

  const handlePageClick = async (pageNumber) => {
    if (pageNumber === currentPage || isFetchingRef.current || pageNumber < 1 || pageNumber > totalPages) return;

    window.scrollTo({ top: 0, behavior: "smooth" });

    if (tokensHistory[pageNumber] !== undefined) {
      setCurrentPage(pageNumber);
      return;
    }

    setLoading(true);
    let tempPage = currentPage;
    let tempToken = nextPageToken;

    while (tempPage < pageNumber && tempToken) {
      tempPage++;
      tokensHistory[tempPage] = tempToken;

      if (tempPage === pageNumber) break;

      try {
        const url = `https://www.googleapis.com/youtube/v3/search?key=${API_KEY}&channelId=${CHANNEL_ID}&part=id&order=date&maxResults=12&type=video&pageToken=${tempToken}`;
        const res = await fetch(url);
        const data = await res.json();
        tempToken = data.nextPageToken || null;
      } catch (e) {
        break;
      }
    }

    setTokensHistory({ ...tokensHistory });
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pages = [];

    if (currentPage > 2) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageClick(1)}
          disabled={loading}
          className={`w-10 h-10 rounded-xl font-medium border text-sm transition bg-black border-gray-800 text-gray-400 hover:text-white hover:border-gray-600`}
        >
          1
        </button>
      );
      if (currentPage > 3) {
        pages.push(<span key="ellipsis-start" className="text-gray-600 px-1">...</span>);
      }
    }

    for (let i = currentPage - 1; i <= currentPage + 2; i++) {
      if (i > 0 && i <= totalPages) {
        if (i === 1 && currentPage > 2) continue;
        if (i === totalPages && currentPage + 2 >= totalPages) continue;

        pages.push(
          <button
            key={i}
            onClick={() => handlePageClick(i)}
            disabled={loading}
            className={`w-10 h-10 rounded-xl font-medium border text-sm transition ${
              currentPage === i
                ? "bg-brand-rougevif border-brand-rougevif text-white font-bold"
                : "bg-black border-gray-800 text-gray-400 hover:text-white hover:border-gray-600"
            }`}
          >
            {i}
          </button>
        );
      }
    }

    if (currentPage + 2 < totalPages) {
      if (currentPage + 3 < totalPages) {
        pages.push(<span key="ellipsis-end" className="text-gray-600 px-1">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageClick(totalPages)}
          disabled={loading}
          className="w-10 h-10 rounded-xl font-medium border text-sm transition bg-black border-gray-800 text-gray-400 hover:text-white hover:border-gray-600"
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  const SkeletonCard = () => (
    <div className="overflow-hidden rounded-2xl border border-gray-800 bg-black shadow-2xl animate-pulse">
      <div className="aspect-video bg-gray-900" />
      <div className="p-6 space-y-4">
        <div className="h-5 bg-gray-800 rounded w-5/6" />
        <div className="space-y-2">
          <div className="h-4 bg-gray-900 rounded" />
          <div className="h-4 bg-gray-900 rounded w-2/3" />
        </div>
        <div className="h-4 bg-gray-800 rounded w-1/3 pt-2" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-noirprofond text-white">
      <header className="bg-black pt-32 pb-24">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-brand-rougefonce to-brand-rougevif bg-clip-text text-transparent">
            Mes Vidéos Youtube
          </h1>
        </div>
      </header>

      <section className="relative py-16 overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              radial-gradient(circle at top right, #E63946, transparent 20%),
              radial-gradient(circle at bottom left, #E63946, transparent 20%)
            `,
          }}
        ></div>

        <div className="relative max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          {error && <div className="text-center text-red-500 py-12">{error}</div>}

          {!error && (
            <>
              <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
                {loading
                  ? Array.from({ length: 12 }).map((_, i) => <SkeletonCard key={i} />)
                  : videos.map((video) => (
                      <article
                        key={video.id}
                        className="overflow-hidden rounded-2xl border border-gray-800 bg-black shadow-2xl"
                      >
                        <div className="aspect-video">
                          <iframe
                            src={video.url}
                            title={video.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="h-full w-full"
                          ></iframe>
                        </div>

                        <div className="p-6">
                          <h2
                            className="text-xl font-semibold text-white line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: video.title }}
                          />
                          <p className="mt-3 text-sm leading-6 text-gray-400 line-clamp-3">
                            {video.description}
                          </p>
                          <a
                            href={video.watchUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-5 inline-flex items-center text-brand-rougevif transition hover:text-white"
                          >
                            Voir sur YouTube
                            <svg className="ml-2 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </a>
                        </div>
                      </article>
                    ))}
              </div>

              {/* BARRE DE PAGINATION AVEC FENÊTRE GLISSANTE */}
              {totalPages > 1 && (
                <div className="mt-16 flex justify-center items-center gap-2 flex-wrap">
                  {/* Flèche Précédent */}
                  <button
                    onClick={() => handlePageClick(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                    className={`p-2.5 rounded-xl border border-gray-800 transition ${
                      currentPage > 1 && !loading
                        ? "bg-black text-white hover:bg-brand-rougevif hover:border-brand-rougevif"
                        : "bg-gray-900 text-gray-600 cursor-not-allowed"
                    }`}
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  {/* Appel de notre fonction de filtrage dynamique */}
                  {renderPageNumbers()}

                  {/* Flèche Suivant */}
                  <button
                    onClick={() => handlePageClick(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                    className={`p-2.5 rounded-xl border border-gray-800 transition ${
                      currentPage < totalPages && !loading
                        ? "bg-black text-white hover:bg-brand-rougevif hover:border-brand-rougevif"
                        : "bg-gray-900 text-gray-600 cursor-not-allowed"
                    }`}
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Videos;